import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { HiPencilAlt, HiBriefcase, HiArrowRight, HiLocationMarker } from "react-icons/hi";
import { openPositions } from "../data/jobsData";
import { useApplicationForm } from "../hooks/useApplicationForm";
import ApplicationFormModal from "../components/ApplicationFormModal";

const DesignUXPage = () => {
  const { showForm, setShowForm, formData, handleChange, resetForm } = useApplicationForm();

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  const handleApply = () => setShowForm(true);

  const roles = openPositions.filter(pos => pos.department === "Design & UX");

  return (
    <div className="pt-2 pb-20 bg-white">
      <ApplicationFormModal showForm={showForm} setShowForm={setShowForm} formData={formData} handleChange={handleChange} resetForm={resetForm} />
      {/* Hero */}
      <div className="bg-gradient-to-br from-pink-600 to-purple-600 py-32 text-center text-white">
        <h1 className="text-6xl font-extrabold">Design & UX at EFA</h1>
        <p className="mt-6 text-xl max-w-3xl mx-auto opacity-90">
          Shape beautiful human-centered experiences that millions can use.
        </p>

        <motion.button
          onClick={handleApply}
          whileHover={{ scale: 1.05 }}
          className="mt-10 bg-white text-pink-600 px-10 py-4 rounded-xl font-bold shadow-xl"
        >
          <HiPencilAlt className="inline-block mr-2" />
          Apply Now
        </motion.button>
      </div>

      {/* Positions */}
      <section className="py-20 bg-white">
        <div className="container-custom text-center mb-10">
          <h2 className="text-4xl font-bold text-gray-900">
            Open Design & UX Roles
          </h2>
        </div>

        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto px-4">
            {roles.map((role, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -6, boxShadow: "0 15px 30px rgba(0,0,0,0.1)" }}
                className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 flex flex-col"
              >
                {/* Header */}
                <div className="mb-3">
                  <div className="inline-flex items-center gap-2 mb-2">
                    <HiBriefcase className="w-4 h-4 text-pink-600" />
                    <span className="text-xs font-bold uppercase tracking-wider text-pink-600">
                      Design & UX
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                    {role.title}
                  </h3>
                </div>

                {/* Meta Tags */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  <span className="px-2.5 py-0.5 bg-pink-50 text-pink-700 rounded-full text-xs font-semibold">
                    Full-Time
                  </span>
                  <span className="px-2.5 py-0.5 bg-purple-50 text-purple-700 rounded-full text-xs font-semibold">
                    Hybrid
                  </span>
                </div>

                {/* Location */}
                <div className="flex items-start text-xs text-gray-600 mb-4">
                  <HiLocationMarker className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span>{role.location}</span>
                </div>

                {/* Description */}
                <p className="text-gray-700 text-sm mb-4 leading-relaxed flex-grow line-clamp-2">
                  {role.description}
                </p>

                {/* Key Skills */}
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Key Skills:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {role.requirements.slice(0, 3).map((req, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-gray-100 rounded text-gray-700 text-xs font-medium"
                      >
                        {req}
                      </span>
                    ))}
                    {role.requirements.length > 3 && (
                      <span className="px-2 py-1 bg-gray-100 rounded text-gray-600 text-xs font-medium">
                        +{role.requirements.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Apply Button */}
                <motion.button
                  onClick={handleApply}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 text-white px-4 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-200"
                >
                  Apply Now <HiArrowRight className="w-4 h-4" />
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignUXPage;
